/* eslint-disable @typescript-eslint/no-explicit-any */
import { Plus, X } from "lucide-react";

import { Button, Input, Select } from "../ds";
import { ColorButton, ColorRow, NumberRow, Row, Section, SegRow, SliderRow, ToggleRow } from "./FormatControls";

/* v2.8 chart extras: display.chart.referenceLines, display.chart.trendLine
 * (object form) and display.chart.pieLabels. Nothing is written until the
 * user touches a control; unset = the chart renders exactly as before. */

type SetFn = (path: string[], value: any, live?: boolean) => void;
type RefLine = { value?: number; stat?: string; axis?: string; series?: string; label?: string; color?: string; style?: string; width?: number; showLabel?: boolean };

const MAX_REF_LINES = 6;
const REF_MODES = [
  { value: "value", label: "Value" },
  { value: "average", label: "Average" },
  { value: "median", label: "Median" },
  { value: "min", label: "Min" },
  { value: "max", label: "Max" },
];
const STYLES = [{ value: "dashed", label: "Dashed" }, { value: "solid", label: "Solid" }, { value: "dotted", label: "Dotted" }];

function modeOf(l: RefLine): string {
  return l.stat && l.stat !== "target" ? l.stat : "value";
}

/** Horizontal (vertical on horizontal bars) lines on the value axis. */
export function ReferenceLinesSection({ c, set, seriesNames, dual }: { c: any; set: SetFn; seriesNames: string[]; dual: boolean }) {
  const lines: RefLine[] = Array.isArray(c.referenceLines) ? c.referenceLines : [];
  const write = (next: RefLine[]) => set(["chart", "referenceLines"], next.length ? next : undefined);
  const patch = (i: number, p: Partial<RefLine>, live = false) =>
    set(["chart", "referenceLines"], lines.map((l, j) => (j === i ? { ...l, ...p } : l)), live);
  return (
    <Section id="reflines" title="Reference lines" summary={lines.length ? `${lines.length} line${lines.length === 1 ? "" : "s"}` : "None"} canReset={lines.length > 0} onReset={() => write([])}>
      <div className="ds-small ds-faint" style={{ marginBottom: 6 }}>A target, budget or average drawn across the chart.</div>
      {lines.map((l, i) => {
        const mode = modeOf(l);
        return (
          <div key={i} className="st-refline" style={{ border: "1px solid var(--ds-border, rgba(128,128,128,.3))", borderRadius: 8, padding: "6px 8px", marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span className="ds-small ds-muted">Line {i + 1}</span>
              <button type="button" className="st-mini" aria-label={`Remove reference line ${i + 1}`} onClick={() => write(lines.filter((_, j) => j !== i))}><X size={12} /></button>
            </div>
            <SegRow
              label="Draw at"
              value={mode}
              options={REF_MODES}
              onChange={(v) => patch(i, v === "value" ? { stat: undefined, value: l.value ?? 0 } : { stat: v, value: undefined })}
            />
            {mode === "value" ? (
              <NumberRow label="Value" value={l.value ?? null} placeholder="0" onChange={(v) => patch(i, { value: v ?? undefined })} />
            ) : seriesNames.length > 1 ? (
              <Row label="Of series" hint="Default: every series on the axis." stacked>
                <Select inputSize="sm" value={l.series ?? ""} onChange={(e) => patch(i, { series: e.target.value || undefined })}>
                  <option value="">All series</option>
                  {seriesNames.map((n) => <option key={n} value={n}>{n}</option>)}
                </Select>
              </Row>
            ) : null}
            {dual ? (
              <SegRow label="Axis" value={l.axis === "secondary" ? "secondary" : "primary"} options={[{ value: "primary", label: "Left" }, { value: "secondary", label: "Right" }]} onChange={(v) => patch(i, { axis: v === "primary" ? undefined : v })} />
            ) : null}
            <Row label="Label" hint="Empty = automatic." stacked>
              <Input inputSize="sm" value={l.label ?? ""} placeholder={mode === "value" ? "e.g. Target" : "e.g. Average"} onChange={(e) => patch(i, { label: e.target.value || undefined }, true)} />
            </Row>
            <ToggleRow label="Show the label" checked={l.showLabel !== false} onChange={(v) => patch(i, { showLabel: v ? undefined : false })} />
            <ColorRow label="Colour" value={l.color} autoLabel="Amber" onChange={(v) => patch(i, { color: v }, true)} />
            <SegRow label="Style" value={(l.style ?? "dashed") as string} options={STYLES} onChange={(v) => patch(i, { style: v === "dashed" ? undefined : v })} />
            <SliderRow label="Thickness" value={l.width} defaultValue={1.5} min={1} max={4} step={0.5} unit="px" onChange={(v) => patch(i, { width: v }, true)} />
          </div>
        );
      })}
      {lines.length < MAX_REF_LINES ? (
        <Button size="sm" variant="ghost" icon={Plus} onClick={() => write([...lines, { stat: "average" }])}>Add a line</Button>
      ) : (
        <div className="ds-small ds-faint">Up to {MAX_REF_LINES} lines.</div>
      )}
    </Section>
  );
}

/** display.chart.trendLine as an object (column / line / area). */
export function TrendLineSection({ c, set, seriesNames }: { c: any; set: SetFn; seriesNames: string[] }) {
  const t = c.trendLine && typeof c.trendLine === "object" ? c.trendLine : undefined;
  const on = !!t && t.show !== false;
  const upd = (p: Record<string, any>, live = false) => set(["chart", "trendLine"], { ...(t ?? {}), show: true, ...p }, live);
  return (
    <Section id="trendline" title="Trend line" summary={on ? "On" : "Off"} canReset={!!t} onReset={() => set(["chart", "trendLine"], undefined)}>
      <ToggleRow label="Show a trend line" hint="Straight best-fit line over the categories. Needs 3+ points; not on stacked charts." checked={on} onChange={(v) => set(["chart", "trendLine"], v ? { ...(t ?? {}), show: true } : undefined)} />
      {on ? (
        <>
          {seriesNames.length > 1 ? (
            <Row label="Of series" stacked>
              <Select inputSize="sm" value={t.series ?? ""} onChange={(e) => upd({ series: e.target.value || undefined })}>
                <option value="">First series</option>
                {seriesNames.map((n) => <option key={n} value={n}>{n}</option>)}
              </Select>
            </Row>
          ) : null}
          <ColorRow label="Colour" value={t.color} autoLabel="Series colour" onChange={(v) => upd({ color: v }, true)} />
          <SegRow label="Style" value={(t.style ?? "dashed") as string} options={STYLES} onChange={(v) => upd({ style: v === "dashed" ? undefined : v })} />
          <ToggleRow label="Show the equation" checked={t.showEquation === true} onChange={(v) => upd({ showEquation: v ? true : undefined })} />
        </>
      ) : null}
    </Section>
  );
}

type ColorRule = { operator: string; value: number; color: string };
const RULE_OPS = [{ value: "lt", label: "below" }, { value: "lte", label: "at most" }, { value: "gt", label: "above" }, { value: "gte", label: "at least" }, { value: "eq", label: "equals" }, { value: "neq", label: "not" }];

/** display.chart.colorRules -- colour single-series bars by value. */
export function ColorRulesSection({ c, set }: { c: any; set: SetFn }) {
  const rules: ColorRule[] = Array.isArray(c.colorRules) ? c.colorRules : [];
  const write = (next: ColorRule[], live = false) => set(["chart", "colorRules"], next.length ? next : undefined, live);
  const patch = (i: number, p: Partial<ColorRule>, live = false) => write(rules.map((r, j) => (j === i ? { ...r, ...p } : r)), live);
  return (
    <Section id="colorrules" title="Colour by value" summary={rules.length ? `${rules.length} rule${rules.length === 1 ? "" : "s"}` : "Off"} canReset={rules.length > 0} onReset={() => write([])}>
      <div className="ds-small ds-faint" style={{ marginBottom: 6 }}>Single-series bars only. First matching rule wins; colours set per category win over a rule.</div>
      {rules.map((r, i) => (
        <div key={i} className="st-band">
          <ColorButton compact value={r.color} onChange={(v) => patch(i, { color: v ?? "#ef4444" }, true)} />
          <Select inputSize="sm" value={r.operator} aria-label="Condition" onChange={(e) => patch(i, { operator: e.target.value })}>
            {RULE_OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          <input className="ds-input ds-input--sm st-numbox" defaultValue={r.value} inputMode="decimal" aria-label="Value" onBlur={(e) => { const n = parseFloat(e.target.value); if (Number.isFinite(n)) patch(i, { value: n }); }} />
          <button type="button" className="st-mini" aria-label="Remove rule" onClick={() => write(rules.filter((_, j) => j !== i))}><X size={12} /></button>
        </div>
      ))}
      {rules.length < 8 ? <Button size="sm" variant="ghost" icon={Plus} onClick={() => write([...rules, { operator: "lt", value: 0, color: "#ef4444" }])}>Add a rule</Button> : null}
    </Section>
  );
}

/** display.chart.pieLabels -- value / percent on the slices (inside the Slice labels section). */
export function PieLabelControls({ c, set }: { c: any; set: SetFn }) {
  const p = c.pieLabels && typeof c.pieLabels === "object" ? c.pieLabels : undefined;
  const on = !!p && p.show !== false;
  const upd = (patch: Record<string, any>, live = false) => set(["chart", "pieLabels"], { ...(p ?? {}), ...patch }, live);
  return (
    <>
      <div className="st-fsub">Numbers on the slices</div>
      <ToggleRow label="Show value / %" checked={on} onChange={(v) => set(["chart", "pieLabels"], v ? { content: "percent" } : undefined)} />
      {on ? (
        <>
          <SegRow label="Show" value={(p.content ?? "percent") as string} options={[{ value: "percent", label: "%" }, { value: "value", label: "Value" }, { value: "both", label: "Both" }]} onChange={(v) => upd({ content: v })} />
          <SegRow label="Where" hint="Small slices get no label inside." value={p.position === "outside" ? "outside" : "inside"} options={[{ value: "inside", label: "Inside" }, { value: "outside", label: "Outside" }]} onChange={(v) => upd({ position: v === "inside" ? undefined : v })} />
          <SliderRow label="Text size" value={p.fontSize} defaultValue={7} min={5} max={14} onChange={(v) => upd({ fontSize: v }, true)} />
          <ColorRow label="Text colour" value={p.color} onChange={(v) => upd({ color: v }, true)} />
        </>
      ) : null}
    </>
  );
}
